import { combineLatest, skip } from 'rxjs';
import { bgLayer$, preservedSeasonLayers$, visibleAdditionalRouteLayers$, visibleLayers$, } from '../stores/store-map.js';
import { season$ } from '../stores/store-main.js';
import { isEmbed, isLayersStoringEnabled, } from '../urlHandling/urlSearchParams.js';
import { isLandLayer, isLandCategoryUrlPrams } from '../layers/layerHelpers.js';
const STORAGE_KEY = 'mapLayerSelection';
export function isSeasonSpecificLayer(layer) {
    return isLandLayer(layer) || isLandCategoryUrlPrams(layer);
}
export function setPreservedOtherSeasonLayers(stored, activeSeason) {
    preservedSeasonLayers$.next(activeSeason === 'summer'
        ? (stored.winterLayers ?? [])
        : (stored.summerLayers ?? []));
}
export function getStoredLayersForSeason(stored, season) {
    const seasonLayers = season === 'summer'
        ? (stored.summerLayers ?? [])
        : (stored.winterLayers ?? []);
    return [...seasonLayers, ...(stored.additionalLayers ?? [])];
}
export function applyStoredRouteState(stored, options) {
    if (options.applyVisibleLayers) {
        visibleLayers$.next(getStoredLayersForSeason(stored, options.activeSeason));
    }
    if (options.applyBgLayer) {
        bgLayer$.next(stored.bgLayer);
    }
    if (options.applyAdditionalRouteLayers) {
        visibleAdditionalRouteLayers$.next(stored.additionalRouteLayers);
    }
    setPreservedOtherSeasonLayers(stored, options.activeSeason);
}
export function readStoredMapState() {
    if (isPersistenceDisabled()) {
        return null;
    }
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw)
        return null;
    return JSON.parse(raw);
}
function writeStoredMapState(selection) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(selection));
}
let writesSuppressed = false;
function runWithoutPersistingWrites(operation) {
    const previous = writesSuppressed;
    writesSuppressed = true;
    try {
        operation();
    }
    finally {
        writesSuppressed = previous;
    }
}
function isPersistenceDisabled() {
    return isEmbed() || !isLayersStoringEnabled;
}
export function buildSelectionSnapshot(layers, bgLayer, additionalRouteLayers, season, preservedLayers) {
    const activeSeason = layers.filter(isSeasonSpecificLayer);
    const additionalLayers = layers.filter((l) => !isSeasonSpecificLayer(l));
    const otherSeason = preservedLayers.filter(isSeasonSpecificLayer);
    return {
        summerLayers: season === 'summer' ? activeSeason : otherSeason,
        winterLayers: season === 'winter' ? activeSeason : otherSeason,
        additionalLayers,
        bgLayer,
        season,
        additionalRouteLayers,
    };
}
export function applyStoredMapState() {
    if (isPersistenceDisabled())
        return;
    const stored = readStoredMapState();
    if (!stored)
        return;
    runWithoutPersistingWrites(() => {
        season$.next(stored.season ?? 'summer');
        applyStoredRouteState(stored, {
            activeSeason: stored.season,
            applyVisibleLayers: true,
            applyBgLayer: true,
            applyAdditionalRouteLayers: true,
        });
    });
}
export function switchActiveSeason(newSeason) {
    if (isPersistenceDisabled()) {
        season$.next(newSeason);
        return;
    }
    const stored = readStoredMapState();
    runWithoutPersistingWrites(() => {
        season$.next(newSeason);
        if (stored) {
            applyStoredRouteState(stored, {
                activeSeason: newSeason,
                applyVisibleLayers: true,
                applyBgLayer: true,
                applyAdditionalRouteLayers: true,
            });
        }
    });
    writeStoredMapState({
        ...buildSelectionSnapshot(visibleLayers$.value, bgLayer$.value, visibleAdditionalRouteLayers$.value, season$.value, preservedSeasonLayers$.value),
        ...(stored?.position ? { position: stored.position } : {}),
    });
}
export function addLayerToStoredSelection(layer) {
    if (isPersistenceDisabled())
        return;
    const stored = readStoredMapState();
    if (!stored) {
        const seasonal = isSeasonSpecificLayer(layer);
        writeStoredMapState({
            summerLayers: seasonal && season$.value === 'summer' ? [layer] : [],
            winterLayers: seasonal && season$.value === 'winter' ? [layer] : [],
            additionalLayers: seasonal ? [] : [layer],
            bgLayer: bgLayer$.value,
            season: season$.value,
            additionalRouteLayers: visibleAdditionalRouteLayers$.value,
        });
        return;
    }
    if (isSeasonSpecificLayer(layer)) {
        if (season$.value === 'summer') {
            if (!(stored.summerLayers ?? []).includes(layer)) {
                writeStoredMapState({
                    ...stored,
                    summerLayers: [...(stored.summerLayers ?? []), layer],
                });
            }
        }
        else {
            if (!(stored.winterLayers ?? []).includes(layer)) {
                writeStoredMapState({
                    ...stored,
                    winterLayers: [...(stored.winterLayers ?? []), layer],
                });
            }
        }
    }
    else {
        const additionalLayers = stored.additionalLayers ?? [];
        if (!additionalLayers.includes(layer)) {
            writeStoredMapState({
                ...stored,
                additionalLayers: [...additionalLayers, layer],
            });
        }
    }
}
export function setupLayersPersistence() {
    if (isPersistenceDisabled())
        return;
    combineLatest([
        visibleLayers$,
        bgLayer$,
        visibleAdditionalRouteLayers$,
        season$,
        preservedSeasonLayers$,
    ])
        .pipe(skip(1))
        .subscribe(([layers, bgLayer, additionalRouteLayers, season, preservedLayers]) => {
        if (writesSuppressed)
            return;
        const existingPosition = readStoredMapState()?.position;
        writeStoredMapState({
            ...buildSelectionSnapshot(layers, bgLayer, additionalRouteLayers, season, preservedLayers),
            ...(existingPosition ? { position: existingPosition } : {}),
        });
    });
}
export function resetMapState() {
    if (isPersistenceDisabled())
        return;
    localStorage.removeItem(STORAGE_KEY);
    runWithoutPersistingWrites(() => {
        visibleLayers$.next([]);
        bgLayer$.next('pk');
        visibleAdditionalRouteLayers$.next({
            detours: true,
            photos: true,
            logos: true,
            shooting: true,
        });
        season$.next('summer');
        preservedSeasonLayers$.next([]);
    });
}
export function readStoredPosition() {
    if (isPersistenceDisabled())
        return null;
    const stored = readStoredMapState();
    console.log(stored);
    return stored?.position ?? null;
}
export function setupPositionPersistence(map) {
    if (isPersistenceDisabled())
        return;
    map.on('moveend', () => {
        const view = map.getView();
        const center = view.getCenter();
        const resolution = view.getResolution();
        if (!center || resolution === undefined)
            return;
        const stored = readStoredMapState();
        if (!stored)
            return;
        writeStoredMapState({
            ...stored,
            position: {
                E: Math.round(center[0]),
                N: Math.round(center[1]),
                resolution,
            },
        });
    });
}
//# sourceMappingURL=mapStateStorage.js.map